import { Injectable, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { AuthStoreService } from '@app/core/services/auth-store.service';
import { StorageService } from '@app/core/services/storage.service';
import { AnalyticsService } from './analytics.service';
import { resolveAnalyticsRoute } from './analytics-route-policy';
import { RegistrationFunnelAnalyticsService } from './registration-funnel-analytics.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsNavigationService {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthStoreService);
  private readonly storage = inject(StorageService);
  private readonly analytics = inject(AnalyticsService);
  private readonly registration = inject(RegistrationFunnelAnalyticsService);
  private started = false;
  private transition = 0;
  private lastViewedRoute: string | null = null;

  start(): void {
    if (this.started || !this.analytics.enabled) return;
    this.started = true;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.transition++;
        // Suppress SDK events and begin Replay teardown before the next view renders.
        void this.analytics.suspend();
      } else if (event instanceof NavigationEnd) {
        void this.activateSuccessfulRoute();
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        void this.restoreAfterFailedNavigation();
      }
    });
    if (this.router.navigated) void this.activateSuccessfulRoute();
  }

  private async activateSuccessfulRoute(): Promise<void> {
    const transition = ++this.transition;
    const decision = resolveAnalyticsRoute(
      this.router.routerState.snapshot,
      this.auth.role(),
      this.auth.user()?.isImpersonating === true || this.storage.getImpersonationSession() !== null
    );
    const ready = await this.analytics.setRoute(decision);
    if (!ready || transition !== this.transition) return;
    if (!decision.policy.pageView || !decision.routeTemplate) {
      this.lastViewedRoute = null;
      return;
    }
    if (decision.routeTemplate === this.lastViewedRoute) return;
    this.analytics.track('Page Viewed');
    if (decision.routeTemplate === '/register') {
      this.registration.once('started', 'Registration Started', { step: 'connect' });
    } else if (decision.routeTemplate === '/billing/success') {
      this.registration.once('checkout_returned', 'Checkout Returned', { step: 'checkout_return' });
    } else if (decision.routeTemplate === '/billing/canceled') {
      this.registration.once('checkout_canceled', 'Checkout Canceled', { step: 'checkout_return', result: 'canceled' });
    }
    this.lastViewedRoute = decision.routeTemplate;
  }

  private async restoreAfterFailedNavigation(): Promise<void> {
    const transition = ++this.transition;
    const decision = resolveAnalyticsRoute(
      this.router.routerState.snapshot,
      this.auth.role(),
      this.auth.user()?.isImpersonating === true || this.storage.getImpersonationSession() !== null
    );
    await this.analytics.setRoute(decision);
    if (transition !== this.transition) return;
    // The user never left the page, so this is not another page view.
  }
}
